import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { Select } from '../components/Select.tsx';

interface StatusResponse {
  connected: boolean;
  emailAddress: string | null;
  scopes: string[];
  hasSendScope: boolean;
  calendarId: string | null;
  connectedAt: string | null;
  oauthClientConfigured: boolean;
  pubsubConfigured: boolean;
  smtp2goConfigured: boolean;
  outboundProvider: 'gmail' | 'smtp2go' | 'none';
  callbackUrl: string;
}

interface CalendarOption {
  id: string;
  summary: string;
  primary: boolean;
  accessRole: string;
}

export function AdminGooglePage(): React.ReactElement {
  const { agent } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<CalendarOption[] | null>(null);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const connectedParam = searchParams.get('connected');
    if (connectedParam === '1') {
      const msg = searchParams.get('msg');
      // The OAuth callback tries to auto-start the Gmail watch so inbound
      // works without a second click. Communicate the outcome so the admin
      // knows whether they're done or still need to nudge something.
      if (msg === 'watch_started') {
        setInfo('Google account connected and Gmail watch started — inbound is live.');
      } else if (msg === 'watch_failed') {
        setError(
          'Google connected, but the auto-start of the Gmail watch failed. Click "Start / renew watch" below and check the server logs.',
        );
      } else if (msg === 'watch_pubsub_missing') {
        setError(
          'Google connected, but Gmail Pub/Sub env vars (GMAIL_PUBSUB_TOPIC / GMAIL_PUBSUB_VERIFICATION_TOKEN) are not set, so inbound is not wired. Set them in DigitalOcean.',
        );
      } else if (msg === 'watch_scope_missing') {
        setError(
          'Google connected, but the gmail.readonly scope was not granted. Click Reconnect and approve all requested permissions.',
        );
      } else {
        setInfo('Google account connected.');
      }
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('msg');
      setSearchParams(next, { replace: true });
    } else if (connectedParam === '0') {
      const msg = searchParams.get('msg') ?? 'unknown_error';
      setError(`Connection failed: ${msg}`);
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('msg');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function loadStatus() {
    try {
      const res = await apiFetch<StatusResponse>('/api/admin/google/status');
      setStatus(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load status.');
    }
  }

  useEffect(() => {
    void loadStatus();
  }, []);

  async function loadCalendars() {
    setCalendarsLoading(true);
    try {
      const res = await apiFetch<{ calendars: CalendarOption[] }>('/api/admin/google/calendars');
      setCalendars(res.calendars);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load calendars.');
    } finally {
      setCalendarsLoading(false);
    }
  }

  function connect() {
    window.location.href = '/api/admin/google/connect';
  }

  async function disconnect() {
    if (!confirm('Disconnect Google account? Inbound email and calendar sync will stop.')) return;
    setBusy(true);
    try {
      await apiFetch('/api/admin/google/disconnect', { method: 'POST' });
      setCalendars(null);
      await loadStatus();
      setInfo('Google account disconnected.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to disconnect.');
    } finally {
      setBusy(false);
    }
  }

  async function setCalendar(id: string | null) {
    setBusy(true);
    try {
      await apiFetch('/api/admin/google/calendar', { method: 'PUT', json: { calendarId: id } });
      await loadStatus();
      setInfo(id ? 'Calendar selected.' : 'Calendar cleared.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update calendar.');
    } finally {
      setBusy(false);
    }
  }

  async function startWatch() {
    setBusy(true);
    try {
      const res = await apiFetch<{ status: string; expiration: string }>(
        '/api/inbound/gmail/watch',
        { method: 'POST' },
      );
      setInfo(`Watch started. Expires ${new Date(res.expiration).toLocaleString()}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start watch.');
    } finally {
      setBusy(false);
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }
  if (!status) return <p className="text-[var(--color-muted)]">Loading…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">Team Google integration</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          One Google account powers both inbound email (Gmail) and project calendar sync.
        </p>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="text-sm text-green-900">{info}</p>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Prerequisites</h2>
        <ul className="mt-2 space-y-1 text-sm">
          <li className={status.oauthClientConfigured ? 'text-green-700' : 'text-[var(--color-danger)]'}>
            {status.oauthClientConfigured ? '✓' : '✗'} GOOGLE_OAUTH_CLIENT_ID and SECRET set in env
          </li>
          <li className={status.pubsubConfigured ? 'text-green-700' : 'text-[var(--color-danger)]'}>
            {status.pubsubConfigured ? '✓' : '✗'} GMAIL_PUBSUB_TOPIC and VERIFICATION_TOKEN set in env
          </li>
        </ul>
        <div className="mt-3 rounded-[var(--radius-button)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted)]">
          <p className="font-semibold text-[var(--color-body)]">Add this exact callback URL to your OAuth client's authorized redirect URIs:</p>
          <p className="mt-1 break-all font-mono">{status.callbackUrl}</p>
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Connection</h2>
            {status.connected ? (
              <div className="mt-2 space-y-1 text-sm">
                <p>
                  <span className="text-[var(--color-muted)]">Connected as:</span>{' '}
                  <strong>{status.emailAddress}</strong>
                </p>
                {status.connectedAt && (
                  <p className="text-xs text-[var(--color-muted)]">
                    Connected {new Date(status.connectedAt).toLocaleString()}
                  </p>
                )}
                <p className="text-xs text-[var(--color-muted)]">
                  Scopes: {status.scopes.join(', ') || '(none)'}
                </p>
              </div>
            ) : (
              <p className="mt-2 text-sm text-[var(--color-muted)]">Not connected.</p>
            )}
          </div>
          {status.connected ? (
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={connect} disabled={busy}>
                Reconnect
              </Button>
              <Button variant="outline" onClick={() => void disconnect()} disabled={busy}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={connect} disabled={busy || !status.oauthClientConfigured}>
              Connect Google account
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Outbound email diagnostic</h2>
        <div className="mt-2 text-sm">
          Active provider:{' '}
          <strong>
            {status.outboundProvider === 'gmail'
              ? `Gmail (${status.emailAddress})`
              : status.outboundProvider === 'smtp2go'
                ? 'SMTP2Go (fallback)'
                : 'None — outbound mail will fail'}
          </strong>
        </div>
        {status.connected && !status.hasSendScope && (
          <div className="mt-2 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Google is connected but the <code>gmail.send</code> scope wasn't granted. Click
            <strong> Reconnect</strong> above and approve all requested scopes to enable Gmail
            outbound. Until then, email will only work if SMTP2Go env vars are set.
          </div>
        )}
        <TestEmailForm />
      </Card>

      {status.connected && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Inbound email watch</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Binds the connected Gmail inbox to the configured Pub/Sub topic. Gmail watches expire after 7 days —
            re-run to renew.
          </p>
          <div className="mt-3">
            <Button variant="secondary" onClick={() => void startWatch()} disabled={busy}>
              Start / renew watch
            </Button>
          </div>
        </Card>
      )}

      {status.connected && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Project calendar</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Project due dates and cutover dates will be written to this calendar. Pick the team calendar
            you've granted the connected account write access to.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
            {calendars === null ? (
              <Button variant="outline" onClick={() => void loadCalendars()} disabled={calendarsLoading}>
                {calendarsLoading ? 'Loading…' : 'Load calendars'}
              </Button>
            ) : (
              <Select
                label="Calendar"
                value={status.calendarId ?? ''}
                onChange={(e) => void setCalendar(e.target.value || null)}
                disabled={busy}
              >
                <option value="">— none —</option>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.summary}{c.primary ? ' (primary)' : ''} — {c.accessRole}
                  </option>
                ))}
              </Select>
            )}
          </div>
          {status.calendarId && (
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              Active calendar ID: <span className="font-mono">{status.calendarId}</span>
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

function TestEmailForm(): React.ReactElement {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [resultKind, setResultKind] = useState<'ok' | 'err' | null>(null);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    setResultKind(null);
    try {
      const res = await apiFetch<{ ok: boolean; provider: string; messageId?: string; error?: string }>(
        '/api/admin/google/test-email',
        { method: 'POST', json: { to } },
      );
      if (res.ok) {
        setResult(`Sent via ${res.provider}. Message id: ${res.messageId ?? '(none)'}.`);
        setResultKind('ok');
      } else {
        setResult(`Failed via ${res.provider}: ${res.error ?? 'unknown'}`);
        setResultKind('err');
      }
    } catch (e) {
      setResult(e instanceof ApiError ? e.message : 'Test failed.');
      setResultKind('err');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={send} className="mt-3 flex flex-wrap items-end gap-2">
      <input
        type="email"
        required
        placeholder="you@example.com"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
      />
      <Button type="submit" disabled={busy || !to}>
        {busy ? 'Sending…' : 'Send test'}
      </Button>
      {result && (
        <div
          className={
            'mt-2 w-full rounded-[var(--radius-button)] p-3 text-sm ' +
            (resultKind === 'ok'
              ? 'border border-green-300 bg-green-50 text-green-900'
              : 'border border-red-300 bg-red-50 text-red-900')
          }
        >
          {result}
        </div>
      )}
    </form>
  );
}
